import { SlidersHorizontal } from "lucide-react"
import { useState } from "react"
import type { AudienceVariable, VariableFilter, VariableFilterOp } from "@/entities/audience"
import { Button } from "@/shared/ui/button"
import { FilterChip } from "@/shared/ui/filter-chip"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"

export const OP_LABELS: Record<VariableFilterOp, string> = {
	set: "заполнена",
	empty: "не заполнена",
	equals: "равна",
	contains: "содержит",
}

export function describeVariableFilter(filter: VariableFilter): string {
	return `${filter.variable} ${OP_LABELS[filter.op]}${filter.value ? ` «${filter.value}»` : ""}`
}

/** Условие по одной переменной: заполнена, пуста, равна или содержит. */
export function VariableFilterPopover({
	variables,
	value,
	onApply,
}: {
	variables: AudienceVariable[]
	value: VariableFilter | null
	onApply: (filter: VariableFilter | null) => void
}) {
	const [open, setOpen] = useState(false)
	const [variable, setVariable] = useState(value?.variable ?? "")
	const [op, setOp] = useState<VariableFilterOp>(value?.op ?? "set")
	const [text, setText] = useState(value?.value ?? "")
	const needsValue = op === "equals" || op === "contains"
	const selected = variable || variables[0]?.name || ""

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant={value ? "secondary" : "outline"}
					className="h-10 shrink-0 rounded-xl"
					aria-label="Фильтр по ответу"
				>
					<SlidersHorizontal />
					<span className="hidden sm:inline">Фильтр</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-72 space-y-3 rounded-2xl p-3">
				<p className="text-sm font-medium">Фильтр по ответу</p>
				{variables.length === 0 ? (
					<p className="text-xs text-muted-foreground">
						В сценарии бота нет переменных: фильтровать не по чему.
					</p>
				) : (
					<form
						className="space-y-2"
						onSubmit={(event) => {
							event.preventDefault()
							if (needsValue && !text.trim()) return
							onApply({ variable: selected, op, ...(needsValue ? { value: text.trim() } : {}) })
							setOpen(false)
						}}
					>
						<NativeSelect
							aria-label="Переменная"
							value={selected}
							onChange={(event) => setVariable(event.target.value)}
						>
							{variables.map((item) => (
								<option key={item.name} value={item.name}>
									{item.name}
								</option>
							))}
						</NativeSelect>
						<NativeSelect
							aria-label="Условие"
							value={op}
							onChange={(event) => setOp(event.target.value as VariableFilterOp)}
						>
							{(Object.keys(OP_LABELS) as VariableFilterOp[]).map((item) => (
								<option key={item} value={item}>
									{OP_LABELS[item]}
								</option>
							))}
						</NativeSelect>
						{needsValue ? (
							<Input
								aria-label="Значение"
								placeholder="Значение"
								value={text}
								onChange={(event) => setText(event.target.value)}
							/>
						) : null}
						<div className="flex justify-end gap-2 pt-1">
							{value ? (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										onApply(null)
										setOpen(false)
									}}
								>
									Сбросить
								</Button>
							) : null}
							<Button type="submit" size="sm" disabled={needsValue && !text.trim()}>
								Применить
							</Button>
						</div>
					</form>
				)}
			</PopoverContent>
		</Popover>
	)
}

export function VariableFilterChip({
	filter,
	onRemove,
}: {
	filter: VariableFilter
	onRemove: () => void
}) {
	return <FilterChip onRemove={onRemove}>{describeVariableFilter(filter)}</FilterChip>
}
